import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, WarningIcon } from '@phosphor-icons/react';

import { TerminalStatus } from '@/modules/backoffice/types/terminal';
import { LinkIcon, UnlinkIcon } from '../../operation-card/operation-icons';
import styles from './terminal-list-selection-bar.module.css';
import type { ActionConfig, TerminalListSelectionBarProps } from './terminal-list-selection-bar.type';

const useActionConfig = (
  status: TerminalListSelectionBarProps['currentStatus'],
): ActionConfig => {
  const { t } = useTranslation();

  const map: Partial<Record<TerminalStatus, ActionConfig>> = {
    [TerminalStatus.AVAILABLE]: {
      label: t('backoffice.terminalsListActionLinkTerminal'),
      icon: <LinkIcon />,
    },
    [TerminalStatus.LINKED]: {
      label: t('backoffice.terminalsListActionAssociateSeller'),
      icon: <LinkIcon />,
      secondaryLabel: t('backoffice.terminalsListActionUnlink'),
      secondaryIcon: <UnlinkIcon />,
    },
    [TerminalStatus.ACTIVE]: {
      label: t('backoffice.terminalsListActionDisassociateSeller'),
      icon: <UnlinkIcon />,
    },
  };

  return map[status] ?? null;
};

export const TerminalListSelectionBar = ({
  selectedCount,
  currentStatus,
  onClearSelection,
  onAction,
  onSecondaryAction,
  onTestSuccess,
  onTestFailure,
}: TerminalListSelectionBarProps) => {
  const { t } = useTranslation();
  const action = useActionConfig(currentStatus);
  const isTestStatus = currentStatus === TerminalStatus.TEST;

  if (selectedCount <= 0) return null;

  return createPortal(
    <div className={styles['bottom-bar']}>
      <div className={styles['bottom-bar-left']}>
        <span className={styles['bottom-bar-count']}>
          {t('backoffice.terminalsSelectionCount', { count: selectedCount })}
        </span>

        {action && (
          <button
            type="button"
            className={`${styles['bottom-bar-action-btn']} ${styles['bottom-bar-action-btn--dark']}`}
            onClick={onAction}
          >
            {action.icon}
            {action.label}
          </button>
        )}

        {action?.secondaryLabel && (
          <button
            type="button"
            className={styles['bottom-bar-action-btn']}
            onClick={onSecondaryAction}
          >
            {action.secondaryIcon}
            {action.secondaryLabel}
          </button>
        )}

        {isTestStatus && (
          <>
            <button
              type="button"
              className={`${styles['bottom-bar-action-btn']} ${styles['bottom-bar-action-btn--dark']}`}
              onClick={onTestSuccess}
            >
              <CheckCircleIcon size={16} weight="fill" />
              {t('backoffice.terminalsBatchTestSuccessBtn')}
            </button>
            <button
              type="button"
              className={`${styles['bottom-bar-action-btn']} ${styles['bottom-bar-action-btn--failure']}`}
              onClick={onTestFailure}
            >
              <WarningIcon size={16} weight="fill" />
              {t('backoffice.terminalsBatchTestFailureBtn')}
            </button>
          </>
        )}
      </div>

      <button
        type="button"
        className={styles['bottom-bar-cancel-btn']}
        onClick={onClearSelection}
      >
        {t('backoffice.terminalsSelectionCancel')}
      </button>
    </div>,
    document.body,
  );
};
